import { formatExpiryDates } from "src/helpers";
import { NonOrderProductCheckInColumnHeader } from "src/enums";
import { NonOrderProductCheckIn, TableColumn } from "src/types";
import { TextWithLineBreaks } from "src/components";
import { Table } from "src/components";

interface NonOrderProductCheckInTableProps {
  nonOrderProductCheckIns: NonOrderProductCheckIn[];
}

const renderBreakdownHeader = (label: string) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-xs">{label}</span>
    <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
      <span className="text-violet-300">UK</span>
      <span className="text-orange-300">EU</span>
    </div>
  </div>
);

const renderBreakdownCell = (total: number | undefined, uk: number | undefined, eu: number | undefined) => {
  const hasSplit = uk !== undefined || eu !== undefined;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="font-semibold tabular-nums text-sm text-secondary-900">{total !== undefined ? total : "-"}</span>
      {hasSplit && (
        <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
          <span className="text-right text-violet-600">{uk ?? "-"}</span>
          <span className="text-left text-orange-600">{eu ?? "-"}</span>
        </div>
      )}
    </div>
  );
};

const NonOrderProductCheckInTable: React.FC<NonOrderProductCheckInTableProps> = ({ nonOrderProductCheckIns }) => {
  const breakdownCellStyle = { minWidth: "110px", textAlign: "center" as const };

  const showWarehouseBreakdown = nonOrderProductCheckIns.some(
    (row) => row.receivedUkQty !== undefined || row.receivedEuQty !== undefined
  );

  const columns: TableColumn<NonOrderProductCheckInColumnHeader, NonOrderProductCheckIn>[] = [
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_ID,
      column: (row) => <span className="font-medium text-secondary-900">{row.productId}</span>
    },
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_NAME,
      column: (row) => <span className="font-semibold text-secondary-900">{row.productName}</span>
    },
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_SKU,
      column: (row) => <span className="font-mono text-secondary-700">{row.productSku ?? "-"}</span>
    },
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_BARCODE,
      column: (row) => <span className="font-mono text-secondary-700">{row.productBarcode}</span>
    },
    {
      header: showWarehouseBreakdown
        ? renderBreakdownHeader(NonOrderProductCheckInColumnHeader.RECEIVED_QTY)
        : NonOrderProductCheckInColumnHeader.RECEIVED_QTY,
      column: showWarehouseBreakdown
        ? (row: NonOrderProductCheckIn) => renderBreakdownCell(row.receivedQty, row.receivedUkQty, row.receivedEuQty)
        : (row: NonOrderProductCheckIn) => (
            <span className="font-semibold text-secondary-900">{row.receivedQty?.toString() ?? "-"}</span>
          ),
      styles: showWarehouseBreakdown ? breakdownCellStyle : undefined
    },
    {
      header: NonOrderProductCheckInColumnHeader.EXPIRY_DATES,
      column: (row) => <TextWithLineBreaks text={formatExpiryDates(row.expiryDates)} />
    }
  ];

  return (
    <Table<NonOrderProductCheckInColumnHeader, NonOrderProductCheckIn>
      data={nonOrderProductCheckIns}
      columns={columns}
    />
  );
};

export { NonOrderProductCheckInTable };
